import { movies, featuredGenres, featuredRegions } from '@/mocks/movies';
import type { Movie } from '@/types/movie';

export interface MovieFilters {
  genre?: string;
  region?: string;
  year?: string;
  minRating?: number;
  sort?: 'latest' | 'hot' | 'rating';
}

export const allMovies = movies as Movie[];

export function getMovieById(id: string): Movie | undefined {
  return allMovies.find((movie) => movie.id === id);
}

export function getMoviesByType(type: string): Movie[] {
  if (type === 'movie' || type === 'tv') {
    return allMovies.filter((movie) => movie.type === type);
  }
  return allMovies;
}

export function getAllGenres(): string[] {
  return Array.from(new Set(allMovies.flatMap((movie) => movie.genres)));
}

export function getAllRegions(): string[] {
  return Array.from(new Set(allMovies.map((movie) => movie.region)));
}

export function getAllYears(): number[] {
  return Array.from(new Set(allMovies.map((movie) => movie.year))).sort((a, b) => b - a);
}

export function sortMovies(list: Movie[], sort: MovieFilters['sort'] = 'latest'): Movie[] {
  const cloned = [...list];
  if (sort === 'hot') {
    return cloned.sort((a, b) => b.playCount - a.playCount);
  }
  if (sort === 'rating') {
    return cloned.sort((a, b) => b.rating - a.rating);
  }
  return cloned.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function filterMovies(list: Movie[], filters: MovieFilters): Movie[] {
  return list.filter((movie) => {
    if (filters.genre && !movie.genres.includes(filters.genre)) return false;
    if (filters.region && movie.region !== filters.region) return false;
    if (filters.year && String(movie.year) !== filters.year) return false;
    if (filters.minRating && movie.rating < filters.minRating) return false;
    return true;
  });
}

export function queryMovies(list: Movie[], keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return list.filter((movie) => {
    const haystack = [
      movie.title,
      movie.subtitle,
      movie.director,
      movie.region,
      movie.language,
      ...movie.cast,
      ...movie.genres,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRecommendations(movie: Movie, limit = 6): Movie[] {
  const scored = allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.type === movie.type) score += 2;
      score += item.genres.filter((genre) => movie.genres.includes(genre)).length * 3;
      if (item.region === movie.region) score += 1;
      if (item.cast.some((actor) => movie.cast.includes(actor))) score += 4;
      score += item.rating / 10;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getLatestMovies(limit = 8): Movie[] {
  return sortMovies(allMovies, 'latest').slice(0, limit);
}

export function getHotMovies(limit = 8): Movie[] {
  return sortMovies(allMovies, 'hot').slice(0, limit);
}

export function getTypeLabel(type: string): string {
  if (type === 'movie') return '电影';
  if (type === 'tv') return '电视剧';
  return '影视';
}

export { featuredGenres, featuredRegions };